import { useNavigate } from "react-router";
import { toast } from "sonner";

import { EnlaceVolver } from "@/components/EnlaceVolver";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { manejarErrorDeUsuario } from "@/components/usuarios/errores";
import { UsuarioFormulario } from "@/components/usuarios/UsuarioFormulario";
import { Skeleton } from "@/components/ui/skeleton";
import { usePerfiles } from "@/hooks/usePerfiles";
import { useCrearUsuario } from "@/hooks/useUsuarios";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";

/**
 * Alta de usuario por invitación (RF-B2): identidad, RUT, cargo, correo,
 * teléfono (opcional) y perfil. SIN contraseña: la define el invitado desde el
 * enlace del correo.
 *
 * El perfil se ELIGE acá y es obligatorio. Antes lo heredaba del default del
 * backend y había que corregirlo después desde la ficha; en esa ventana la
 * persona ya tenía el correo de invitación y entraba con permisos que nadie
 * decidió.
 *
 * Por eso la pantalla ahora depende del maestro de perfiles: sin esa lista no
 * hay con qué elegir, así que espera (o avisa) en vez de ofrecer un alta a la
 * que le falta un campo obligatorio — mismo tratamiento que la ficha.
 */
export function UsuarioNuevo() {
  const navigate = useNavigate();
  const crear = useCrearUsuario();
  const perfiles = usePerfiles();
  const estado = estadoDeConsulta(perfiles);

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <EnlaceVolver to="/admin/usuarios">Volver al listado</EnlaceVolver>

      <p className="text-muted-foreground max-w-[40rem] text-sm">
        Le enviaremos un correo para que defina su contraseña; el enlace vence en 7 días.
        Entrará con el perfil que elijas acá, y se puede cambiar después desde la ficha.
      </p>

      {estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar los perfiles"
          error={perfiles.error}
          onReintentar={() => perfiles.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.sinConexion ? (
        <EstadoSinConexion que="los perfiles" />
      ) : estado.cargando ? (
        <div
          role="status"
          aria-label="Cargando el formulario"
          className="bg-card w-full max-w-[40rem] space-y-5 rounded-xl border p-6"
        >
          <Skeleton className="h-11 w-full rounded-md" />
          <Skeleton className="h-11 w-full rounded-md" />
          <Skeleton className="h-11 w-full rounded-md" />
          <Skeleton className="h-11 w-40 rounded-md" />
        </div>
      ) : (
        <UsuarioFormulario
          pideConfirmacionDeCorreo
          perfiles={perfiles.data ?? []}
          etiquetaEnvio="Crear usuario"
          enviando={crear.isPending}
          onEnviar={(valores, { errorDeCampo }) =>
            crear.mutate(
              {
                first_name: valores.first_name,
                last_name: valores.last_name,
                email: valores.email,
                email_confirmation: valores.email_confirmation,
                rut: valores.rut,
                cargo: valores.cargo,
                // El input vacío manda `""`; lo que la columna tiene que
                // guardar es `null`. El backend lo normaliza igual, pero
                // mandar el tipo correcto evita que el contrato dependa de eso.
                telefono: valores.telefono.trim() || null,
                perfil_id: Number(valores.perfil_id),
              },
              {
                onSuccess: ({ usuario, emailEnviado }) => {
                  if (emailEnviado) {
                    toast.success(`Invitación enviada a ${usuario.email}`);
                  } else {
                    // El usuario existe pero el correo no salió. Decirlo, porque
                    // si no el admin espera una invitación que nunca va a llegar.
                    toast.warning(
                      `${usuario.name} quedó creado, pero no pudimos enviar el correo. Usa "Reenviar invitación" para reintentar.`,
                      { duration: 8000 },
                    );
                  }
                  navigate("/admin/usuarios");
                },
                onError: (error) =>
                  manejarErrorDeUsuario(
                    error,
                    errorDeCampo,
                    "No pudimos crear el usuario. Inténtalo de nuevo.",
                  ),
              },
            )
          }
          onCancelar={() => navigate("/admin/usuarios")}
        />
      )}
    </div>
  );
}
